import React, { useEffect } from "react";
import PropTypes from "prop-types";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

export default function Achievements({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Achievements | Bandarban University",
}) {
  // Update Browser Tab Title
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const rootStyle = {
    "--accent": accent,
    backgroundImage: `linear-gradient(135deg, var(--accent) 0%, rgba(17,94,50,0.85) 40%, rgba(255,255,255,0.03) 100%)`,
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative w-full overflow-hidden px-6 sm:px-8 lg:px-16"
        style={rootStyle}
      >
        <div className="relative max-w-7xl mx-auto flex flex-col items-center text-center py-20 lg:py-28">
          <img
            src={logoSrc}
            alt="Logo"
            className="w-28 h-28 object-contain drop-shadow-md mb-6"
          />

          <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
            Welcome to <span style={{ color: "var(--accent)" }}>Bandarban University</span>
          </h1>

          <p className="mt-4 max-w-2xl text-white/90 text-lg sm:text-xl">
            Building bright minds with a strong foundation in knowledge,
            innovation, and community.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href="#admissions"
              className="px-6 py-3 bg-white rounded-xl font-semibold text-sm shadow hover:opacity-95"
              style={{ color: "var(--accent)" }}
            >
              Apply Now
            </a>
            <a
              href="#about"
              className="px-6 py-3 border border-white/40 rounded-xl text-white text-sm hover:bg-white/10"
            >
              Learn More
            </a>
          </div>
        </div>

        {/* Decorative bottom wave */}
        <div className="-mt-6">
          <svg
            viewBox="0 0 1440 80"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-20"
          >
            <path
              d="M0,32 C240,96 480,0 720,32 C960,64 1200,16 1440,32 L1440 80 L0 80 Z"
              fill="white"
              opacity="0.92"
            />
          </svg>
        </div>
      </section>

      {/* Sub Section Content Body */}
      <section className="bg-white py-16 px-6 sm:px-10 lg:px-20">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold"
            style={{ color: "var(--accent)" }}
          >
            Discover Our Programs
          </h2>
          <p className="mt-4 text-gray-600 max-w-3xl mx-auto">
            Explore a wide range of academic programs designed to help you excel
            in your chosen field and shape your future with confidence.
          </p>
        </div>

        {/* Details Body: 3 Columns */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="p-6 rounded-xl shadow-md border bg-white hover:shadow-lg transition">
            <h3 className="text-xl font-semibold mb-2" style={{ color: "var(--accent)" }}>
              Undergraduate Programs
            </h3>
            <p className="text-gray-700">
              A variety of bachelor’s programs focused on building solid
              academic foundations and practical skills.
            </p>
          </div>

          <div className="p-6 rounded-xl shadow-md border bg-white hover:shadow-lg transition">
            <h3 className="text-xl font-semibold mb-2" style={{ color: "var(--accent)" }}>
              Postgraduate Programs
            </h3>
            <p className="text-gray-700">
              Advanced degrees designed to enhance your expertise and research
              capabilities in specialized areas.
            </p>
          </div>

          <div className="p-6 rounded-xl shadow-md border bg-white hover:shadow-lg transition">
            <h3 className="text-xl font-semibold mb-2" style={{ color: "var(--accent)" }}>
              Professional Courses
            </h3>
            <p className="text-gray-700">
              Short-term and skill-focused courses to help professionals and
              learners gain a competitive edge.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

Achievements.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};
